import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { useRef, useState } from "react";
import { Button } from "./Button.js";
import { Card } from "./Card.js";
/**
 * Parser de preview no cliente — mesma logica de deteccao de separador e campo
 * entre aspas de `apps/api/src/_shared/csv.ts` (fonte de verdade da importacao
 * de verdade). So pra mostrar uma previa fiel ANTES de confirmar o envio — se
 * as regras de parse mudarem no backend, replicar aqui tambem. Duplicado (em
 * vez de importado) porque `packages/ui` nao pode depender de `apps/api` nem
 * de `apps/web` (ver diagrama de dependencias do monorepo no CLAUDE.md).
 */
function parseCsvPreview(text, origem, maxRows = 10) {
    const stripped = text.replace(/^﻿/, "");
    const firstLine = stripped.split(/\r?\n/, 1)[0] ?? "";
    const nComma = (firstLine.match(/,/g) ?? []).length;
    const nSemi = (firstLine.match(/;/g) ?? []).length;
    const nTab = (firstLine.match(/\t/g) ?? []).length;
    let normalized = stripped;
    if (nTab > nComma && nTab > nSemi)
        normalized = stripped.replace(/\t/g, ",");
    else if (nSemi > nComma)
        normalized = stripped.replace(/;/g, ",");
    const raw = [];
    let row = [];
    let field = "";
    let inQuotes = false;
    for (let i = 0; i < normalized.length; i++) {
        const ch = normalized[i];
        if (inQuotes) {
            if (ch === '"') {
                if (normalized[i + 1] === '"') {
                    field += '"';
                    i++;
                }
                else
                    inQuotes = false;
            }
            else {
                field += ch;
            }
        }
        else if (ch === '"')
            inQuotes = true;
        else if (ch === ",") {
            row.push(field);
            field = "";
        }
        else if (ch === "\n") {
            row.push(field);
            raw.push(row);
            row = [];
            field = "";
        }
        else if (ch === "\r") { /* swallow */ }
        else
            field += ch;
    }
    if (field !== "" || row.length > 0) {
        row.push(field);
        raw.push(row);
    }
    if (raw.length === 0)
        return { origem, headers: [], rows: [], totalRows: 0, linhas: {} };
    const headers = (raw[0] ?? []).map((h) => h.trim());
    const dataLines = raw.slice(1).filter((r) => !(r.length === 1 && r[0] === ""));
    const toObj = (r) => {
        const obj = {};
        for (let j = 0; j < headers.length; j++)
            obj[headers[j]] = (r[j] ?? "").trim();
        return obj;
    };
    const rows = dataLines.slice(0, maxRows).map(toObj);
    // Linha 1 = cabecalho, entao a linha de dado N (0-indexed) e' a linha N+2 do
    // arquivo — mesma convencao que o backend usa em todo erro reportado.
    const linhas = {};
    dataLines.forEach((r, i) => { linhas[i + 2] = toObj(r); });
    return { origem, headers, rows, totalRows: dataLines.length, linhas };
}
/** Baixa um relatorio CSV simples (usado pelo card de erros abaixo). */
function baixarCsv(filename, headers, linhas) {
    const esc = (v) => {
        const s = String(v);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const body = [headers.join(","), ...linhas.map((l) => l.map(esc).join(","))].join("\n");
    const blob = new Blob([body], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}
/**
 * Lista de erros de importação agrupada por mensagem (em vez de uma linha por
 * erro) — um arquivo com "matricula invalida" em 40 linhas mostrava 40
 * entradas quase identicas antes disso. Agrupar por mensagem torna o problema
 * sistemico (ex: coluna inteira errada) escaneável em 1 olhada, com contagem e
 * lista de linhas por baixo. Rolagem própria (não estica a página) e um
 * "baixar relatório" pra cruzar com a planilha original fora da tela.
 */
/**
 * Reduz uma mensagem de erro ao seu "molde", removendo o valor especifico da
 * linha (matricula, cpf, numero de contrato) que o backend interpola no texto
 * — ex: `matricula "ABC-001" invalida` e `matricula "ABC-002" invalida` sao o
 * MESMO problema, mas so agrupam se a comparacao ignorar o valor entre aspas
 * e sequencias de 3+ digitos (matricula/cpf/contrato). Numeros curtos (ex:
 * "11 digitos") ficam intactos — fazem parte do texto fixo, nao do dado.
 */
function normalizarMensagem(msg) {
    return msg.replace(/"[^"]*"/g, '"…"').replace(/[\w-]*\d{3,}[\w-]*/g, "…");
}
export function ImportErrorsCard({ errors, linhas, colunasIdentidade, downloadFilename }) {
    if (errors.length === 0)
        return null;
    const identidadeDaLinha = (line) => {
        const row = linhas?.[line];
        if (!row)
            return "";
        const cols = colunasIdentidade && colunasIdentidade.length > 0 ? colunasIdentidade : Object.keys(row).slice(0, 2);
        return cols.map((c) => row[c]).filter(Boolean).join(" · ");
    };
    // Agrupa pelo molde normalizado, mas mostra a mensagem REAL da primeira
    // ocorrencia como rotulo — "…" no lugar do dado ficaria estranho pra ler.
    const grupos = new Map();
    for (const e of errors) {
        const chave = normalizarMensagem(e.message);
        const g = grupos.get(chave);
        if (g)
            g.lines.push(e.line);
        else
            grupos.set(chave, { amostra: e.message, lines: [e.line] });
    }
    const gruposOrdenados = [...grupos.values()].sort((a, b) => b.lines.length - a.lines.length);
    return (_jsxs("div", { style: { border: "1px solid var(--danger-500, #ef4444)", borderRadius: 10, overflow: "hidden" }, children: [_jsxs("div", { style: {
                    display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12,
                    padding: "10px 14px", flexWrap: "wrap",
                    background: "color-mix(in srgb, var(--danger-500, #ef4444) 12%, transparent)",
                }, children: [_jsxs("b", { style: { fontSize: 13, color: "var(--danger-500, #ef4444)" }, children: ["\u26A0 ", errors.length, " linha", errors.length === 1 ? "" : "s", " com erro", gruposOrdenados.length > 1 ? `, em ${gruposOrdenados.length} tipos de problema` : ""] }), _jsx(Button, { size: "sm", variant: "ghost", onClick: () => baixarCsv(downloadFilename ?? "erros-importacao.csv", ["linha", "mensagem", "identificacao"], errors.map((e) => [e.line, e.message, identidadeDaLinha(e.line)])), children: "\u2193 Baixar relat\u00F3rio (CSV)" })] }), _jsx("div", { style: { maxHeight: 280, overflowY: "auto" }, children: gruposOrdenados.map(({ amostra, lines }) => (_jsxs("div", { style: { padding: "8px 14px", borderTop: "1px solid var(--border)" }, children: [_jsxs("div", { style: { fontSize: 12.5 }, children: [_jsxs("b", { children: [lines.length, "\u00D7"] }), " ", amostra] }), _jsxs("div", { style: { fontSize: 11.5, color: "var(--text-muted)", marginTop: 4, lineHeight: 1.7 }, children: [lines.slice(0, 15).map((l, i) => {
                                    const ident = identidadeDaLinha(l);
                                    return (_jsxs("span", { children: [i > 0 ? " · " : "", "linha ", l, ident ? ` (${ident})` : ""] }, l));
                                }), lines.length > 15 ? ` … e mais ${lines.length - 15}` : ""] })] }, amostra))) })] }));
}
export function CsvImportPanel({ title, columnsHint, templateUrl, templateLabel, layoutTecnicoUrl, colunasIdentidade, onImport, onImported }) {
    const [open, setOpen] = useState(false);
    const [pasted, setPasted] = useState("");
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [preview, setPreview] = useState(null);
    // Sobrevive ao "setPreview(null)" do confirmar — o card de erros do
    // resultado ainda precisa consultar o conteudo original pra dar contexto.
    const [linhasArquivo, setLinhasArquivo] = useState({});
    const [dragOver, setDragOver] = useState(false);
    const inputRef = useRef(null);
    function stage(text, origem) {
        setError(null);
        setResult(null);
        const p = parseCsvPreview(text, origem);
        setPreview(p);
        setLinhasArquivo(p.linhas);
    }
    async function onFile(file) {
        const text = await file.text();
        setPasted(text);
        stage(text, file.name);
    }
    function cancelarPreview() {
        setPreview(null);
    }
    async function confirmarImportacao() {
        if (!preview)
            return;
        setError(null);
        setBusy(true);
        try {
            const r = await onImport(pasted);
            setResult(r);
            setPreview(null);
            if ((r.inserted + r.updated) > 0)
                onImported?.(r);
        }
        catch (e) {
            setError(e.message);
        }
        finally {
            setBusy(false);
        }
    }
    function onDrop(e) {
        e.preventDefault();
        setDragOver(false);
        const file = e.dataTransfer.files?.[0];
        if (file)
            void onFile(file);
    }
    return (_jsxs(Card, { children: [_jsxs("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }, children: [_jsxs("div", { children: [_jsx("div", { style: { fontSize: 11, fontWeight: 700, letterSpacing: ".06em", color: "var(--text-dim)", textTransform: "uppercase" }, children: "Importar CSV" }), _jsx("div", { style: { fontWeight: 700, marginTop: 2 }, children: title }), columnsHint ? _jsx("div", { style: { fontSize: 12, color: "var(--text-muted)", marginTop: 4 }, children: columnsHint }) : null] }), _jsxs("div", { style: { display: "flex", gap: 8, flexWrap: "wrap" }, children: [_jsx("a", { href: templateUrl, download: true, style: { textDecoration: "none" }, children: _jsx(Button, { size: "sm", variant: "ghost", children: templateLabel ?? "↓ Baixar exemplo" }) }), layoutTecnicoUrl ? (_jsx("a", { href: layoutTecnicoUrl, target: "_blank", rel: "noreferrer", style: { textDecoration: "none" }, children: _jsx(Button, { size: "sm", variant: "ghost", title: "Dicion\u00E1rio de campos em PDF: tipo, formato, obrigatoriedade e regras. Envie para quem for gerar o arquivo.", children: "Layout t\u00E9cnico (PDF)" }) })) : null, _jsx(Button, { size: "sm", onClick: () => {
                                    setOpen((o) => !o);
                                    setPreview(null);
                                    setResult(null);
                                    setError(null);
                                }, children: open ? "Cancelar" : "Importar CSV" })] })] }), open ? (_jsxs("div", { style: { marginTop: 14, display: "flex", flexDirection: "column", gap: 10 }, children: [!preview ? (_jsxs("div", { onDragOver: (e) => { e.preventDefault(); setDragOver(true); }, onDragLeave: () => setDragOver(false), onDrop: onDrop, onClick: () => inputRef.current?.click(), onKeyDown: (e) => { if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            inputRef.current?.click();
                        } }, role: "button", tabIndex: 0, style: {
                            border: `2px dashed ${dragOver ? "var(--accent)" : "var(--border-strong)"}`,
                            borderRadius: 10,
                            padding: "28px 16px",
                            textAlign: "center",
                            cursor: "pointer",
                            background: dragOver ? "color-mix(in srgb, var(--accent) 8%, transparent)" : "var(--bg-elev-2)",
                            transition: "border-color .15s, background .15s",
                        }, children: [_jsx("div", { style: { fontSize: 13, fontWeight: 600 }, children: "Arraste o arquivo CSV aqui, ou clique pra escolher" }), _jsx("div", { style: { fontSize: 12, color: "var(--text-muted)", marginTop: 4 }, children: "Nada \u00E9 enviado ainda \u2014 voc\u00EA confere os dados antes de confirmar." }), _jsx("input", { ref: inputRef, type: "file", accept: ".csv,text/csv", onChange: (e) => {
                                    const f = e.target.files?.[0];
                                    if (f)
                                        void onFile(f);
                                    e.target.value = "";
                                }, style: { display: "none" } })] })) : (_jsxs("div", { style: { display: "flex", flexDirection: "column", gap: 10 }, children: [_jsxs("div", { style: { fontSize: 13 }, children: [_jsx("b", { children: preview.origem }), " \u2014 ", preview.totalRows, " linha", preview.totalRows === 1 ? "" : "s", " de dado, ", preview.headers.length, " coluna", preview.headers.length === 1 ? "" : "s", ".", preview.totalRows === 0 ? (_jsx("span", { style: { color: "var(--danger-500, #ef4444)" }, children: " Nenhuma linha de dado encontrada \u2014 confira se o arquivo tem cabe\u00E7alho e pelo menos uma linha." })) : null] }), preview.headers.length > 0 ? (_jsx("div", { style: { overflowX: "auto", border: "1px solid var(--border)", borderRadius: 8 }, children: _jsxs("table", { style: { borderCollapse: "collapse", width: "100%", fontSize: 12 }, children: [_jsx("thead", { children: _jsx("tr", { style: { background: "var(--bg-elev-2)" }, children: preview.headers.map((h) => (_jsx("th", { style: { textAlign: "left", padding: "6px 10px", fontWeight: 700, whiteSpace: "nowrap", borderBottom: "1px solid var(--border)" }, children: h }, h))) }) }), _jsx("tbody", { children: preview.rows.map((r, i) => (_jsx("tr", { children: preview.headers.map((h) => (_jsx("td", { style: { padding: "6px 10px", borderTop: "1px solid var(--border)", whiteSpace: "nowrap" }, children: r[h] || "—" }, h))) }, i))) })] }) })) : null, preview.totalRows > preview.rows.length ? (_jsxs("div", { style: { fontSize: 12, color: "var(--text-dim)" }, children: ["Mostrando as primeiras ", preview.rows.length, " de ", preview.totalRows, " linhas. O arquivo inteiro \u00E9 enviado ao confirmar."] })) : null, _jsxs("div", { style: { display: "flex", gap: 8 }, children: [_jsx(Button, { size: "sm", onClick: confirmarImportacao, disabled: busy || preview.totalRows === 0, children: busy ? "Importando…" : "Confirmar importação" }), _jsx(Button, { size: "sm", variant: "ghost", onClick: cancelarPreview, disabled: busy, children: "Escolher outro arquivo" })] })] })), error ? _jsx("p", { style: { color: "var(--danger-500, #ef4444)", fontSize: 13 }, children: error }) : null, result ? (_jsxs("div", { style: { display: "flex", flexDirection: "column", gap: 10 }, children: [_jsxs("div", { style: { padding: 12, background: "var(--bg-elev-2)", borderRadius: 8, fontSize: 13 }, children: ["\u2713 Importa\u00E7\u00E3o conclu\u00EDda \u2014 ", _jsx("b", { children: result.inserted }), " inseridos \u00B7 ", _jsx("b", { children: result.updated }), " atualizados \u00B7 ", _jsx("b", { children: result.skipped }), " pulados"] }), _jsx(ImportErrorsCard, { errors: result.errors, linhas: linhasArquivo, colunasIdentidade: colunasIdentidade, downloadFilename: `erros-${title.toLowerCase().replace(/\s+/g, "-")}.csv` })] })) : null] })) : null] }));
}
//# sourceMappingURL=CsvImportPanel.js.map